import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { TYPO_DEFAULTS, HEADING_FONTS, BODY_FONTS, loadGoogleFont, applyTypography } from "@/lib/typography";

function FontPicker({ label, value, options, onChange, testid }) {
  const custom = !options.includes(value);
  return (
    <div className="space-y-2">
      <label className="label-eyebrow text-slate-500 block">{label}</label>
      <select value={custom ? "__custom" : value} data-testid={`${testid}-select`}
        onChange={(e) => onChange(e.target.value === "__custom" ? "" : e.target.value)}
        className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
        {options.map((f) => <option key={f} value={f}>{f}</option>)}
        <option value="__custom">Custom Google Font…</option>
      </select>
      {custom && (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Exact Google Font name, e.g. Spectral"
          data-testid={`${testid}-custom-input`} />
      )}
    </div>
  );
}

function Preview({ t }) {
  const heading = { fontFamily: `"${t.heading_font}", Georgia, serif` };
  const body = { fontFamily: `"${t.body_font}", sans-serif`, fontSize: `${t.base_size}px` };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-8 space-y-4" data-testid="typography-preview">
      <div className="label-eyebrow text-npst-gold">Live Preview</div>
      <h1 style={{ ...heading, fontSize: `${t.base_size * 2.5}px` }} className="font-bold text-npst-navy leading-tight">
        National Public Service Trust
      </h1>
      <h2 style={{ ...heading, fontSize: `${t.base_size * 1.5}px` }} className="font-semibold text-npst-ink">
        Public Interest Litigation Repository
      </h2>
      <p style={body} className="text-slate-600 leading-relaxed">
        The Trust works to strengthen constitutional governance, accountability and access to justice through
        research, representations and public interest litigation before courts and statutory authorities.
      </p>
      <p style={{ ...body, fontSize: `${t.base_size * 0.875}px` }} className="text-slate-500">
        Small text · 0123456789 · ₹ 1,00,000 · Receipt NPST/DR/2025-26/0001
      </p>
    </div>
  );
}

export default function TypographyAdmin() {
  const [t, setT] = useState(TYPO_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/settings/typography").then(({ data }) => setT(data)).catch(() => {}); }, []);
  useEffect(() => {
    const id = setTimeout(() => { loadGoogleFont(t.heading_font); loadGoogleFont(t.body_font); }, 500);
    return () => clearTimeout(id);
  }, [t.heading_font, t.body_font]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/typography", t);
      applyTypography(data);
      toast.success("Typography saved — live on the website");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <div>
      <AdminHeader title="Typography" subtitle="Google Fonts and base text size for the public website"
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setT(TYPO_DEFAULTS)} data-testid="typography-reset-btn">Reset to default</Button>
          <Button onClick={save} disabled={saving} className="bg-npst-navy text-white" data-testid="typography-save-btn">{saving ? "Saving…" : "Save Typography"}</Button>
        </div>} />
      <AdminPage>
        <div className="grid lg:grid-cols-[360px_1fr] gap-8">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6">
            <FontPicker label="Headings font" value={t.heading_font} options={HEADING_FONTS} onChange={(v) => set("heading_font", v)} testid="heading-font" />
            <FontPicker label="Body font" value={t.body_font} options={BODY_FONTS} onChange={(v) => set("body_font", v)} testid="body-font" />
            <div className="space-y-3">
              <label className="label-eyebrow text-slate-500 flex justify-between">Base font size <span data-testid="base-size-value">{t.base_size}px</span></label>
              <Slider min={12} max={22} step={1} value={[t.base_size]} onValueChange={([v]) => set("base_size", v)} data-testid="base-size-slider" />
              <p className="text-xs text-slate-500">All text on the website scales proportionally. Default is 16px.</p>
            </div>
            <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer" className="text-xs text-npst-gold underline" data-testid="browse-google-fonts-link">Browse Google Fonts</a>
          </div>
          <Preview t={t} />
        </div>
      </AdminPage>
    </div>
  );
}
